import { useCallback, useMemo } from "react";
import hash from "object-hash";

import {
  Autotriage_Verdict,
  autotriage_VerdictFromJSON,
  autotriage_VerdictToNumber,
} from "@protos/ai/v1/triage";
import { Severity } from "@protos/common/v1/common";
import { IssuesFilter, IssueTab } from "@protos/issues/v1/issues";
import {
  DEFAULT_BRANCHES_GROUP,
  SECRETS_FINDINGS_PAGE,
} from "@shared/constants";
import { useStateParams, useSupportsFeature } from "@shared/hooks";

function issueTabToQueryParam(issueTab: IssueTab): string {
  switch (issueTab) {
    case IssueTab.OPEN:
      return "open";
    case IssueTab.IGNORED:
      return "ignored";
    case IssueTab.REVIEWING:
      return "reviewing";
    case IssueTab.FIXING:
      return "fixing";
    case IssueTab.CLOSED:
      return "closed";
    default:
      return "unspecified";
  }
}

function issueTabFromQueryParam(queryParam: string): IssueTab {
  switch (queryParam) {
    case "open":
      return IssueTab.OPEN;
    case "ignored":
      return IssueTab.IGNORED;
    case "reviewing":
      return IssueTab.REVIEWING;
    case "fixing":
      return IssueTab.FIXING;
    case "closed":
      return IssueTab.CLOSED;
    default:
      return IssueTab.UNSPECIFIED;
  }
}

function severityToQueryParam(severity: Severity): string | null {
  switch (severity) {
    case Severity.CRITICAL:
      return "3";
    case Severity.HIGH:
      return "2";
    case Severity.MEDIUM:
      return "1";
    case Severity.LOW:
      return "0";
    default:
      return null;
  }
}

function severityFromQueryParam(queryParam: string): Severity | null {
  // uses the int values of the IssueSeverity server-defined enum, kept around for URL backwards-compat
  switch (queryParam) {
    case "3":
      return Severity.CRITICAL;
    case "2":
      return Severity.HIGH;
    case "1":
      return Severity.MEDIUM;
    case "0":
      return Severity.LOW;
    default:
      return null;
  }
}

export function aiVerdictToQueryParam(aiVerdict: Autotriage_Verdict): string {
  return autotriage_VerdictToNumber(aiVerdict).toString();
}

export function aiVerdictFromQueryParam(
  queryParam: string
): Autotriage_Verdict {
  return autotriage_VerdictFromJSON(parseInt(queryParam));
}

/**
 * Generates and manages state for any finding filters which apply to ALL issue types (sast, sca, etc).
 */
export const useCommonIssuesFilterState = (logFromPage: string) => {
  const hasPrimaryBranchCapabilities = useSupportsFeature(
    "hasPrimaryBranchCapabilities"
  );

  const [repo, setRepo] = useStateParams<IssuesFilter["repositoryNames"]>(
    [],
    "repo",
    (s) => s.join(","),
    (s) => (s ? s.split(",") : []),
    logFromPage
  );

  const [repoRef, setRepoRef] = useStateParams<
    IssuesFilter["repositoryRefIds"]
  >(
    [],
    "repo_ref",
    (s) => s.join(","),
    (s) => {
      return s ? s.split(",").map((n) => parseInt(n, 10)) : [];
    },
    logFromPage
  );

  const [primary, setPrimary] = useStateParams<IssuesFilter["onPrimaryBranch"]>(
    undefined,
    "primary",
    (s) => (s === true ? "true" : s === false ? "false" : ""),
    (s) => (s === "true" ? true : s === "false" ? false : undefined),
    logFromPage
  );

  const [team, setTeam] = useStateParams<IssuesFilter["teamNames"]>(
    [],
    "team",
    (s) => s.join(","),
    (s) => (s ? s.split(",") : []),
    logFromPage
  );

  const [repoTag, setRepoTag] = useStateParams<IssuesFilter["repositoryTags"]>(
    [],
    "repo_tag",
    (s) => s.join(","),
    (s) => (s ? s.split(",") : []),
    logFromPage
  );

  const [tab, setTab] = useStateParams<NonNullable<IssuesFilter["tab"]>>(
    IssueTab.OPEN,
    "tab",
    (s) => issueTabToQueryParam(s),
    (s) => issueTabFromQueryParam(s),
    logFromPage
  );

  const [severities, setSeverities] = useStateParams<
    IssuesFilter["severities"]
  >(
    [],
    "severity",
    (severityValues) =>
      severityValues
        .map(severityToQueryParam)
        .flatMap((s) => s || [])
        .join(","),
    (severityStr) =>
      severityStr
        .split(",")
        .map(severityFromQueryParam)
        .flatMap((s) => s || []),
    logFromPage
  );

  const [aiVerdicts, setAiVerdicts] = useStateParams<
    IssuesFilter["aiVerdicts"]
  >(
    [],
    "ai_verdict",
    (verdicts) => verdicts.map(aiVerdictToQueryParam).join(","),
    (verdictStr) => verdictStr.split(",").map(aiVerdictFromQueryParam),
    logFromPage
  );

  const [aiSensitivity, setAiSensitivity] = useStateParams<
    IssuesFilter["aiSensitivities"]
  >(
    [],
    "ai_sensitivity",
    (s) => s.join(","),
    (s) => (s ? s.split(",") : []),
    logFromPage
  );

  const [ref, setRef] = useStateParams<IssuesFilter["refs"]>(
    [],
    "ref",
    (s) => s.join(","),
    (s) => (s ? s.split(",") : []),
    logFromPage
  );

  /**
   * A repackaged version of this state that is consumable by the issues API endpoints.
   */
  const issuesFilterProto = useMemo<IssuesFilter>(
    () =>
      IssuesFilter.create({
        refs: ref,
        repositoryNames: repo,
        repositoryTags: repoTag,
        repositoryRefIds: repoRef,
        aiVerdicts: aiVerdicts,
        aiSensitivities: aiSensitivity,
        severities: severities,
        tab: tab,
        teamNames: team,
        onPrimaryBranch: primary,
      }),
    [
      aiSensitivity,
      aiVerdicts,
      primary,
      ref,
      repo,
      repoRef,
      repoTag,
      severities,
      tab,
      team,
    ]
  );

  /**
   * A hash of all the filters in this object. Useful to detect when any filters have changed
   * without needing to understand the internal state of this hook.
   */
  const filterHash = useMemo(
    () =>
      hash([
        repo,
        tab,
        repoRef,
        repoTag,
        aiVerdicts,
        aiSensitivity,
        severities,
        ref,
        team,
        primary,
      ]),
    [
      aiSensitivity,
      aiVerdicts,
      ref,
      repo,
      repoRef,
      repoTag,
      severities,
      tab,
      team,
      primary,
    ]
  );

  /**
   * `True` if any filter field is set to to non-default values, `False` otherwise.
   */
  const hasFiltersSet =
    repo.length > 0 ||
    ref.length > 0 ||
    repoRef.length > 0 ||
    repoTag.length > 0 ||
    aiVerdicts.length > 0 ||
    aiSensitivity.length > 0 ||
    severities.length > 0 ||
    team.length > 0 ||
    tab !== IssueTab.OPEN ||
    !!primary;

  /**
   * Resets all filter fields to empty, even if by default they are populated
   * (except tab, which cannot be empty)
   */
  const clearFilters = useCallback(() => {
    setRepo([]);
    setRepoRef([]);
    setRepoTag([]);
    setTeam([]);
    setSeverities([]);
    setAiVerdicts([]);
    setAiSensitivity([]);
    setRef([]);
    setTab(IssueTab.OPEN);
    setPrimary(undefined);
  }, [
    setAiSensitivity,
    setAiVerdicts,
    setPrimary,
    setRef,
    setRepo,
    setSeverities,
    setRepoRef,
    setRepoTag,
    setTab,
    setTeam,
  ]);

  /**
   * Resets all filter fields to their default values (empty, nullish, etc).
   */
  const resetFilters = useCallback(() => {
    setRepo([]);
    setRepoRef([]);
    setRepoTag([]);
    setTeam([]);
    setSeverities([]);
    setAiVerdicts([]);
    setAiSensitivity([]);
    setRef(hasPrimaryBranchCapabilities ? [] : [DEFAULT_BRANCHES_GROUP]);
    setTab(IssueTab.OPEN);
    setPrimary(
      hasPrimaryBranchCapabilities && logFromPage !== SECRETS_FINDINGS_PAGE
        ? true
        : undefined
    );
  }, [
    setRepo,
    setRepoRef,
    setRepoTag,
    setTeam,
    setSeverities,
    setAiVerdicts,
    setAiSensitivity,
    setRef,
    hasPrimaryBranchCapabilities,
    setTab,
    setPrimary,
    logFromPage,
  ]);

  return useMemo(
    () => ({
      issuesFilterProto,
      repo,
      setRepo,
      repoTag,
      setRepoTag,
      repoRef,
      setRepoRef,
      team,
      setTeam,
      tab,
      setTab,
      severities,
      setSeverities,
      aiVerdicts,
      setAiVerdicts,
      aiSensitivity,
      setAiSensitivity,
      ref,
      setRef,
      primary,
      setPrimary,
      clearFilters,
      resetFilters,
      hasFiltersSet,
      filterHash,
    }),
    [
      aiSensitivity,
      aiVerdicts,
      filterHash,
      hasFiltersSet,
      issuesFilterProto,
      ref,
      repo,
      repoRef,
      repoTag,
      clearFilters,
      resetFilters,
      setAiSensitivity,
      setAiVerdicts,
      setRef,
      setRepo,
      setRepoRef,
      setRepoTag,
      setSeverities,
      setTab,
      setTeam,
      severities,
      tab,
      team,
      primary,
      setPrimary,
    ]
  );
};
